import React, {
  ComponentProps,
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { COREContainer, COREContainerProps } from "../Layout/COREContainer";
import {
  InteractionMenuItem,
  InteractionProps,
} from "../Layout/COREAccordionItem";
import { COREProgress, COREProgressProps } from "../Feedback/COREProgress";
import { Layout, PlotlyHTMLElement } from "plotly.js/dist/plotly-myPlotly.js";
import { TestID } from "../../shared/testids/testids";
import { AggregatesChartV2 } from "../../modules/chartBuilder/AggregatesChartV2";
import sizeMe from "react-sizeme";
import { COREHeading } from "../Typography/COREHeading";
import { AggregatesWaterfallChart } from "../../modules/chartBuilder/AggregatesWaterfallChart";
import { CORERadarChartAPI } from "./CORERadarChartAPI";
import { Plot } from "./COREChart";
import { CORESunburstChart } from "./CORESunburstChart";
import { CORERadarChart } from "./CORERadarChart";
import { COREDotPlotChart } from "./COREDotPlotChart";
import { COREBasicChart } from "./COREBasicChart";
import { WidgetPanelRender } from "../../modules/dashboards/WidgetPanel";
import { DropdownMenuItem } from "../Form/COREDropdownMenu";
import {
  buildChartActions,
  ChartAction,
  isHeaderAction,
  toChartMenuButton,
  toInteractionButton,
  toMenuItem,
} from "./ChartUtils/chartActions";
import { PriceHistoryChart } from "../../modules/projectMarketplaceLegacy/PriceHistoryChart";
import { useChartFullScreen } from "./ChartUtils/useChartFullScreen";
import "./ChartHOC.less";

export type PlotRefType =
  | {
      el: PlotlyHTMLElement;
      props: ComponentProps<typeof Plot> & {
        layout: Partial<Layout> & { filename?: string };
      };
      resizeHandler: () => void | undefined;
    }
  | undefined;

export type InjectedChartProps = {
  showlegend?: boolean;
  chartTitle?: string;
  chartHoverMode?: Layout["hovermode"];
  headerSection?: React.ReactNode;
  /** Rendered in the container body, above the chart itself. */
  bodySection?: React.ReactNode;
  interactionsMenu?: DropdownMenuItem[];
  /** List injected rows below the chart's own actions instead of above. */
  interactionsMenuLast?: boolean;
  interactionsRight?: InteractionProps[];
  setOnDownloadCSV?: Dispatch<SetStateAction<Function>>;
  setOnDownloadPNG?: Dispatch<SetStateAction<Function>>;
  setOnResetChart?: Dispatch<SetStateAction<Function>>;
  onAfterReset?: () => void;
};

export type WithContainerChartProps = Pick<
  COREContainerProps,
  | "interactionSpace"
  | "className"
  | "fullHeight"
  | "roundCorner"
  | "bordered"
  | "ghost"
  | "flushHeader"
> & {
  testID: TestID;
  downloadCsv?: boolean;
  downloadPng?: boolean;
  resetChart?: boolean;
  percent?: COREProgressProps["percent"];
  /**
   * Set only by charts the user built in Chart Builder, which is what puts the
   * "Edit in Chart Builder" action on them and on nothing else.
   */
  editInChartBuilderId?: number;
};

export type ChartProps = {
  setOnDownloadCSV?: Dispatch<SetStateAction<Function>>;
  setOnDownloadPNG?: Dispatch<SetStateAction<Function>>;
  setOnResetChart?: Dispatch<SetStateAction<Function>>;
  displayTitle?: boolean;
};

export type ContainerStyle = "panel" | "container" | "widget";

export const ChartHOC = <P extends object>(
  containerStyle: ContainerStyle,
  Chart: React.ComponentType<P & ChartProps>
) => {
  const WithContainer: React.ComponentType<
    P & WithContainerChartProps & InjectedChartProps
  > = ({
    testID,
    chartTitle,
    interactionSpace = 8,
    chartHoverMode = "x",
    showlegend = true,
    downloadCsv,
    downloadPng,
    resetChart,
    editInChartBuilderId,
    className,
    percent = 0,
    fullHeight = true,
    roundCorner,
    bordered,
    headerSection,
    bodySection,
    flushHeader,
    interactionsMenu: injectedInteractionsMenu,
    interactionsMenuLast,
    interactionsRight: injectedInteractionsRight,
    ghost,
    setOnDownloadCSV: setOnDownloadCSVProp,
    setOnDownloadPNG: setOnDownloadPNGProp,
    setOnResetChart: setOnResetChartProp,
    onAfterReset,
    ...props
  }: WithContainerChartProps & InjectedChartProps) => {
    const [progressPercent, setProgressPercent] =
      useState<COREProgressProps["percent"]>(percent);
    const plotRef = useRef<PlotRefType>();
    const chartLegendRef = useRef();
    const titleRef = useRef<HTMLDivElement>(null);

    const chartRef = useRef<typeof Chart>(null);

    const [onDownloadCSV, setOnDownloadCSV] = useState<Function>();
    const [onDownloadPNG, setOnDownloadPNG] = useState<Function>();
    const [onResetChart, setOnResetChart] = useState<Function>();

    const wrappedSetOnDownloadCSV: Dispatch<SetStateAction<Function>> =
      useCallback(
        (fn) => {
          setOnDownloadCSV(fn);
          if (setOnDownloadCSVProp) setOnDownloadCSVProp(fn);
        },
        [setOnDownloadCSVProp]
      );

    const wrappedSetOnDownloadPNG: Dispatch<SetStateAction<Function>> =
      useCallback(
        (fn) => {
          setOnDownloadPNG(fn);
          if (setOnDownloadPNGProp) setOnDownloadPNGProp(fn);
        },
        [setOnDownloadPNGProp]
      );

    const wrappedSetOnResetChart: Dispatch<SetStateAction<Function>> =
      useCallback(
        (fn) => {
          setOnResetChart(fn);
          if (setOnResetChartProp) setOnResetChartProp(fn);
        },
        [setOnResetChartProp]
      );

    const handleResetChart = useCallback(() => {
      if (onResetChart) onResetChart();
      if (onAfterReset) onAfterReset();
    }, [onResetChart, onAfterReset]);

    const handleDownloadCSV = useCallback(() => {
      if (onDownloadCSV) onDownloadCSV();
    }, [onDownloadCSV]);

    const handleDownloadPng = useCallback(() => {
      if (onDownloadPNG) onDownloadPNG();
    }, [onDownloadPNG]);

    const { isFullScreen, toggleFullScreen, renderFullScreen } =
      useChartFullScreen();

    // Declared once and rendered two ways below, so the header buttons and the
    // dropdown can never list different actions, labels or orders.
    const chartActions: ChartAction[] = useMemo(
      () =>
        buildChartActions({
          onDownloadCSV:
            downloadCsv && onDownloadCSV ? handleDownloadCSV : undefined,
          onDownloadPNG:
            downloadPng && onDownloadPNG ? handleDownloadPng : undefined,
          // Widgets return through their own path below and never reach this
          // list, so there is no container style to check for here.
          onToggleFullScreen: toggleFullScreen,
          isFullScreen,
          // The chart hands its reset handler over after mount, so the button
          // is always offered and `handleResetChart` no-ops until then.
          onResetChart: handleResetChart,
          editInChartBuilderId,
        }),
      [
        downloadCsv,
        onDownloadCSV,
        handleDownloadCSV,
        downloadPng,
        onDownloadPNG,
        handleDownloadPng,
        toggleFullScreen,
        isFullScreen,
        handleResetChart,
        editInChartBuilderId,
      ]
    );

    // Two feeds off one action list. The header keeps the button row plus a
    // dropdown of its own, while `interactionsMenu` is what the container falls
    // back to once the header is too narrow to show either — which is why the
    // reset action appears in that one and not in the header's dropdown.
    const interactionsRight: InteractionProps[] = useMemo(() => {
      const headerActions = chartActions.filter(isHeaderAction);
      const overflowActions = chartActions.filter(
        (action) => action.placement === "overflow"
      );
      return [
        ...(injectedInteractionsRight ?? []),
        ...headerActions.map((action) => toInteractionButton(action, testID)),
        // A chart with nothing to overflow gets no dropdown rather than an
        // empty one. Injected rows count towards that: they would otherwise
        // only ever appear in the container's collapsed menu.
        ...(overflowActions.length || injectedInteractionsMenu?.length
          ? [
              toChartMenuButton(
                overflowActions,
                testID,
                injectedInteractionsMenu,
                interactionsMenuLast
              ),
            ]
          : []),
      ];
    }, [
      injectedInteractionsRight,
      injectedInteractionsMenu,
      interactionsMenuLast,
      chartActions,
      testID,
    ]);

    const builtInteractionsMenu: InteractionMenuItem[] = useMemo(
      () =>
        interactionsMenuLast
          ? [
              ...chartActions.map((action) => toMenuItem(action, testID)),
              ...(injectedInteractionsMenu ?? []),
            ]
          : [
              ...(injectedInteractionsMenu ?? []),
              ...chartActions.map((action) => toMenuItem(action, testID)),
            ],
      [injectedInteractionsMenu, interactionsMenuLast, chartActions, testID]
    );

    const headerDisplay = headerSection ? headerSection : chartTitle;
    let containerHeader = <div ref={titleRef}>{chartTitle}</div>;
    if (["panel", "widget"].includes(containerStyle)) {
      containerHeader = (
        <COREHeading
          level={3}
          marginBottom={!flushHeader}
          testID={(testID + "-container-header") as TestID}
        >
          <div ref={titleRef}>{headerDisplay}</div>
        </COREHeading>
      );
    }

    const chartWithProps = (
      <Chart
        {...(props as P)}
        ref={chartRef}
        chartTitle={chartTitle}
        downloadCsv={false}
        downloadPng={false}
        chartHoverMode={chartHoverMode}
        showlegend={showlegend}
        setProgressPercent={setProgressPercent}
        chartPanel={true}
        plotRef={plotRef}
        chartIsIn={"containerChart"}
        chartLegendRef={chartLegendRef}
        testID={testID}
        titleRef={titleRef}
        setOnDownloadCSV={wrappedSetOnDownloadCSV}
        setOnDownloadPNG={wrappedSetOnDownloadPNG}
        setOnResetChart={wrappedSetOnResetChart}
        displayTitle={false}
        isWidget={containerStyle === "widget"}
        fitParent={isFullScreen}
      />
    );

    if (containerStyle === "widget") {
      // Widgets offer their downloads unless a caller opts out, where panels
      // offer them only when a caller opts in, so the widget menu is built from
      // its own list rather than the one above.
      const widgetActions = buildChartActions({
        onDownloadCSV:
          downloadCsv !== false && onDownloadCSV
            ? handleDownloadCSV
            : undefined,
        onDownloadPNG:
          downloadPng !== false && onDownloadPNG
            ? handleDownloadPng
            : undefined,
        onToggleFullScreen: toggleFullScreen,
        isFullScreen,
        onResetChart:
          resetChart !== false && onResetChart ? handleResetChart : undefined,
        editInChartBuilderId,
      });

      const chartMenu: DropdownMenuItem[] = [
        ...(injectedInteractionsMenu ?? []),
        ...widgetActions.map((action) => toMenuItem(action, testID)),
      ];

      return renderFullScreen(
        <WidgetPanelRender
          menuItems={chartMenu}
          testID={testID}
          progressPercent={progressPercent}
          header={containerHeader}
        >
          {bodySection && (
            <div className="chart-body-section">{bodySection}</div>
          )}
          {chartWithProps}
        </WidgetPanelRender>
      );
    }

    const container = (
      <COREContainer
        progress={
          <COREProgress
            type={"line"}
            percent={progressPercent}
            testID={testID}
            hideOnEmpty={false}
            hideOnComplete={true}
          />
        }
        header={containerHeader}
        flushHeader={flushHeader}
        interactionSpace={interactionSpace}
        interactionsRight={interactionsRight}
        interactionsMenu={builtInteractionsMenu}
        menuType={"differentMenu"}
        noScroll={true}
        testID={testID}
        fullHeight={fullHeight}
        type={containerStyle}
        roundCorner={roundCorner}
        bordered={bordered}
        ghost={ghost}
      >
        {bodySection && <div className="chart-body-section">{bodySection}</div>}
        {chartWithProps}
      </COREContainer>
    );

    return renderFullScreen(container);
  };
  return sizeMe({ noPlaceholder: true })(WithContainer);
};

export const COREAggregatesContainerChart = ChartHOC(
  "container",
  AggregatesChartV2
);
export const COREAggregatesPanelChart = ChartHOC("panel", AggregatesChartV2);
export const COREAggregatesWidgetChart = ChartHOC("widget", AggregatesChartV2);
export const CORESunburstContainerChart = ChartHOC(
  "container",
  CORESunburstChart
);
export const CORESunburstPanelChart = ChartHOC("panel", CORESunburstChart);
export const CORESunburstWidgetChart = ChartHOC("widget", CORESunburstChart);
export const COREWaterfallContainerChart = ChartHOC(
  "container",
  AggregatesWaterfallChart
);
export const COREWaterfallPanelChart = ChartHOC(
  "panel",
  AggregatesWaterfallChart
);
export const COREWaterfallWidgetChart = ChartHOC(
  "widget",
  AggregatesWaterfallChart
);
export const CORERadarContainerChart = ChartHOC("container", CORERadarChart);
export const CORERadarPanelChart = ChartHOC("panel", CORERadarChart);
export const CORERadarWidgetChart = ChartHOC("widget", CORERadarChart);

export const CORERadarAPIContainerChart = ChartHOC(
  "container",
  CORERadarChartAPI
);

export const CORERadarAPIPanelChart = ChartHOC("panel", CORERadarChartAPI);
export const CORERadarAPIWidgetChart = ChartHOC("widget", CORERadarChartAPI);

export const COREDotPlotContainerChart = ChartHOC(
  "container",
  COREDotPlotChart
);
export const COREDotPlotPanelChart = ChartHOC("panel", COREDotPlotChart);
export const COREDotPlotWidgetChart = ChartHOC("widget", COREDotPlotChart);

export const COREBasicPanelChart = ChartHOC("panel", COREBasicChart);
export const COREBasicContainerChart = ChartHOC("container", COREBasicChart);
export const COREBasicWidgetChart = ChartHOC("widget", COREBasicChart);

export const PriceHistoryWidgetChart = ChartHOC("panel", PriceHistoryChart);
